// Stream parsing utilities from the working chat saga

/**
 * Reset function for starting new messages
 * Simplified since w          // Create SIMPLE placeholder using markdown image syntax only
          // Use a comment marker that we can find and replace later
          const imgTag = `

![Generating image...](data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iIzk0YTNiOCIgc3Ryb2tlPSIjMzc0MTUxIiBzdHJva2Utd2lkdGg9IjIiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzM3NDE1MSIgZm9udC1zaXplPSIxNiI+R2VuZXJhdGluZyBpbWFnZS4uLjwvdGV4dD48L3N2Zz4=)<!-- streaming-img-${imageKey} -->

`;onger use any buffers or detection
 */
export const resetFormatDetection = () => {
  // No buffer to reset since react-markdown handles all formatting
};

/**
 * Unescape JSON-style escape sequences back to real characters
 * This fixes the core issue where streaming JSON contains \\n instead of actual newlines
 */
const unescapeNewlines = (text: string) => {
  return text
    .replace(/\\\\n/g, '\n')   // double-escaped
    .replace(/\\n/g, '\n')     // single-escaped
    .replace(/\\t/g, '\t')
    .replace(/\\"/g, '"')      // escaped quotes
    .replace(/\\\\/g, '\\');   // double backslashes
};

/**
 * Sanitize content to prevent CSS injection and XSS
 * More careful to preserve markdown syntax
 */
export const sanitizeContent = (content: string): string => {
  // Remove potentially dangerous HTML tags and attributes
  // But be careful not to break markdown syntax
  const dangerousPatterns = [
    /<script\b[^<]*(?:(?!<\/script\b)[^<]*)*<\/script\b[^>]*>/gi,
    /<style\b[^<]*(?:(?!<\/style\b)[^<]*)*<\/style\b[^>]*>/gi,
    /javascript:/gi,
    /vbscript:/gi,
    // Only remove data:text/html, not data: in general (markdown images might use data:)
    /data:text\/html/gi,
    // Be more specific with event handlers to avoid breaking markdown
    /\son\w+\s*=/gi, // Remove event handlers like onclick, onload, etc. but only with space prefix
  ];

  let sanitized = content;
  dangerousPatterns.forEach(pattern => {
    sanitized = sanitized.replace(pattern, '');
  });

  return sanitized;
};

const getParseContent = (content: string) => {
  try {
    return JSON.parse(content);
  } catch(e) {
    return null;
  }
};

let errorParseWarnCount = 0;
const streamingDataContentErrorValidation = (content: string) => {
  try {
    const idx = content.indexOf('{"error":');
    if (idx === -1) return null;
    // Scan ahead to find balanced JSON braces starting from idx
    let braceDepth = 0;
    let end = -1;
    for (let i = idx; i < content.length; i++) {
      const ch = content[i];
      if (ch === '{') braceDepth++;
      else if (ch === '}') {
        braceDepth--;
        if (braceDepth === 0) { end = i + 1; break; }
      }
    }
    if (end === -1) return null; // incomplete JSON in current buffer
    const jsonSlice = content.slice(idx, end);
    let errorContent: any;
    try {
      errorContent = JSON.parse(jsonSlice);
    } catch (inner) {
      if (errorParseWarnCount < 3) { errorParseWarnCount++;
      }
      return null;
    }
    // message might already be object or stringified JSON
    let parsedMessage: any = errorContent?.error?.message;
    if (typeof parsedMessage === 'string' && parsedMessage.trim().startsWith('{') && parsedMessage.trim().endsWith('}')) {
      try { parsedMessage = JSON.parse(parsedMessage); } catch {}
    }
    return parsedMessage?.error?.innererror?.code || null;
  } catch (e) {
    if (errorParseWarnCount < 3) { errorParseWarnCount++;
    }
    return null;
  }
};

// Track processed content to avoid duplicates  
let processedContent = '';

// Simplified stream content filtering - just accumulate content and replace images as they update
export const filterStreamContent = (content: string, resetState: boolean = false) => {
  if (resetState) {
    processedContent = '';
    resetFormatDetection();
    return '';
  }

  // If we've already processed this exact content, return empty string
  if (content === processedContent) {
    return '';
  }

  let finalString = "";
  // Maintain a running record of the largest valid base64 we've accepted this invocation to avoid regressions
  let largestImageB64 = '';
  let replacedImageThisPass = false;
  
  // Handle both SSE format (with "data: ") and raw JSON chunks
  const lines = content.includes('data: ') ? content.split('data: ') : [content];
  
  for (let i=0; i < lines.length; i++) {
    // Preserve leading spaces which may carry semantic spacing between tokens; only strip trailing newlines
    const rawLine = lines[i];
    const line = rawLine.replace(/[\r\n]+$/,'');
    if (!line || line === '[DONE]') {
      continue;
    }
    try{
      const errorContent = line.includes('{"error":');
      
      // Microsoft bug with content_filter_error flag appear in the middle of stream
      const contentFilterError = line.includes("content_filter_error") ? true : false;
      
      // Check for safety violations that should be handled gracefully
      const hasSafetyViolation = line.includes('safety_violations') || line.includes('safety system');

      if(errorContent && !contentFilterError){
        const errorCode = streamingDataContentErrorValidation(line);
        if(errorCode) {
          throw new Error(errorCode);
        }
        
        // If we have a safety violation, log it but don't break the stream
        if (hasSafetyViolation) { continue; // Skip this chunk but continue processing
        }
        
        return '';        
      } 
      
      const obj = getParseContent(line);
      
      // Handle streaming image generation - extract from incomplete JSON using string matching
      if (line.includes('"type":"image_generation.partial_image"') && line.includes('"b64_json":"')) {
        // Extract b64 data from incomplete JSON - be more careful with partial data
        const b64Match = line.match(/"b64_json":"([A-Za-z0-9+/=]+)/);
        if (b64Match) {
          let b64Data = b64Match[1];
          // Sanitize: remove any stray newline or slash characters that can slip in mid-stream
          b64Data = b64Data.replace(/\s+/g, '').replace(/\\n/g, '').replace(/\\r/g, '');
          
          // Validate that we have a reasonable amount of base64 data (at least 1000 chars for a partial image)
          if (b64Data.length > 1000) {
            // Only accept if it's larger than any we've seen this pass to prevent regression
            if (b64Data.length > largestImageB64.length) {
              largestImageB64 = b64Data;
              const imageMarkdown = `![Generated Image](data:image/png;base64,${b64Data})`;
              
              // Replace any existing image or add if none exists
              if (finalString.includes('![Generated Image]')) {
                finalString = finalString.replace(/!\[Generated Image\]\([^)]*\)/g, imageMarkdown);
              } else {
                finalString += imageMarkdown;
              }
              replacedImageThisPass = true;
            } // otherwise ignore smaller parser image
          } // otherwise skipping for being too small
        }
      }
      // Handle completed image generation - this has the full correct data
      else if (obj?.type === "image_generation.completed" && obj?.image_data?.b64_json) {
        let completedB64 = obj.image_data.b64_json || '';
        completedB64 = completedB64.replace(/\s+/g, '').replace(/\\n/g, '').replace(/\\r/g, '');
        const isValidB64Format = /^[A-Za-z0-9+/=]+$/.test(completedB64);
        if (!isValidB64Format) { } else {
          let isDecodable = false;
          try {
            const binaryString = atob(completedB64.substring(0, Math.min(1000, completedB64.length)));
            isDecodable = binaryString.length > 0;
          } catch (e) { // Even if decode of first 1000 chars fails (rare), still try to adopt; renderer will handle fallback
            isDecodable = true; // force adoption to break race with partial
          }
          // Force adoption regardless of relative size vs largest partial to avoid second-to-last image race
          if (isDecodable) {
            const imageMarkdown = `![Generated Image](data:image/png;base64,${completedB64})<!-- completed-image -->`;
            if (finalString.includes('![Generated Image]')) {
              finalString = finalString.replace(/!\[Generated Image\]\([^)]*\)(?:<!-- completed-image -->)?/g, imageMarkdown);
            } else {
              finalString += imageMarkdown;
            }
            largestImageB64 = completedB64; // reset tracker to final
            replacedImageThisPass = true;
          }
        }
      }
      // Handle text content
      else if (obj?.type === "content" && obj?.data?.content) {
        finalString += obj.data.content;
      }
      // Keep the old OpenAI format for backwards compatibility
      else if (obj?.choices?.length > 0 && obj.choices[0].delta && obj.choices[0].delta.content) {
        if (obj?.type && obj.type === "img") {
          obj.choices[0].delta.content = "<br /><img tag=\"imgopenai\" style=\"width: 50%; cursor: pointer\" src=\"" + obj.choices[0].delta.content  + "\" /> \n\n";
        }
        finalString += obj.choices[0].delta.content;
      }
    } catch (e) {
        // Log error but don't let parsing errors break the stream
        const errorMessage = (e as Error).message;
        
        // Handle specific error types
        if (errorMessage === 'ResponsibleAIPolicyViolation') { throw new Error(errorMessage);
        }
        
        // For other errors, log and continue processing
        if (lines[i] && lines[i].includes('image_generation')) { } else { }
        
        // Continue processing other chunks instead of failing completely
        continue;
    }
  }
  
  // Calculate only the new content since last processed
  const newContent = finalString.substring(processedContent.length);
  const hasImageData = finalString.includes('data:image/png;base64,');
  
  // If we replaced the image this pass, we must emit the whole finalString so the renderer can re-select
  if (hasImageData && replacedImageThisPass) {
    processedContent = finalString; // advance
    return finalString; // full string required for proper re-selection
  }

  // If no image change, but there is existing image markdown, suppress emission to avoid flicker
  if (hasImageData && !replacedImageThisPass) {
    processedContent = finalString; // still advance so length stays consistent
    return ''; // no new visible change
  }

  // Non-image incremental path
  processedContent = finalString;
  return newContent ? unescapeNewlines(newContent) : '';
};

export const updateCitationsMarking = (responseChunk: string) => {    
  try{
      // Keep markdown fences intact — just clean citations
      return responseChunk?.replace(/\s\[doc(\d+)\]/g, "")?.replace(/\[doc(\d+)\]/g, "");
  }catch(e) {
    return responseChunk;
  }
};